import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, ArrowRight, User } from 'lucide-react';

const Signup = () => {


    const [step, setStep] = useState<'signup' | 'verify'>('signup');
    const [otp, setOtp] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('/api/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            if (response.status === 409) {
                throw new Error('Email already registered');
            }

            if (!response.ok) {
                throw new Error('Registration failed');
            }

            // Success - Move to OTP step
            setStep('verify');
        } catch (err: any) {
            setError(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch('/api/verify-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, otp })
            });

            if (!response.ok) {
                throw new Error('Invalid or expired OTP');
            }

            // Success
            navigate('/login?verified=true');
        } catch (err: any) {
            setError(err.message || 'Verification failed');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        try {
            await fetch('/api/resend-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            alert('OTP resent to your email');
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
            <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-[#009B8F]/10 text-[#009B8F] rounded-full flex items-center justify-center mx-auto mb-4">
                        <UserPlus size={32} />
                    </div>
                    <h2 className="text-2xl font-serif text-[#011E26]">
                        {step === 'signup' ? 'Create Account' : 'Verify Email'}
                    </h2>
                    <p className="text-slate-500 text-sm mt-2">
                        {step === 'signup' ? 'Join NexOverseas today' : `Enter OTP sent to ${email}`}
                    </p>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-6 text-center">
                        {error}
                    </div>
                )}

                {step === 'signup' ? (
                    <form onSubmit={handleSignup} className="space-y-6">
                        <div>
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Email Address</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all"
                                placeholder="Enter your email"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all"
                                placeholder="At least 6 characters"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Confirm Password</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all"
                                placeholder="Re-enter password"
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 bg-[#009B8F] hover:bg-[#00857a] text-white text-sm font-bold tracking-widest uppercase transition-all rounded-lg flex items-center justify-center gap-2 disabled:opacity-70"
                        >
                            {loading ? 'Creating Account...' : (
                                <>Sign Up <ArrowRight size={16} /></>
                            )}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleVerify} className="space-y-6">
                        <div>
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">One-Time Password</label>
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all text-center text-2xl tracking-widest"
                                placeholder="000000"
                                maxLength={6}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 bg-[#009B8F] hover:bg-[#00857a] text-white text-sm font-bold tracking-widest uppercase transition-all rounded-lg flex items-center justify-center gap-2 disabled:opacity-70"
                        >
                            {loading ? 'Verifying...' : 'Verify Email'}
                        </button>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={handleResend}
                                className="text-sm text-[#009B8F] font-bold hover:underline"
                            >
                                Resend OTP
                            </button>
                        </div>
                    </form>
                )}

                <div className="mt-8 text-center">
                    <p className="text-slate-500 text-sm">
                        Already have an account?{' '}
                        <Link to="/login" className="text-[#009B8F] font-bold hover:underline">
                            Log In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Signup;

